import React, { Fragment, useMemo, useState } from "react";

import FormularioRichContent from "../../Formulario/FormularioRichContent";
import { letraDaOpcao } from "../../Formulario/opcoes";
import {
  Avatar,
  Filtros,
  Indicadores,
  Paginacao,
  Pilula,
  Vazio,
  formatarData,
  usePaginacao
} from "../relatorio";

import "./styles.css";

/* Relatório de um formulário: como a turma respondeu cada pergunta e o que
   cada estudante escolheu.

   Formulário não tem resposta certa, então nada aqui fala em acerto: a visão
   "Por pergunta" mostra a distribuição entre as alternativas, e a "Por aluno"
   mostra quem já respondeu e, aberta a linha, a escolha de cada pergunta.

   A barra da distribuição é de uma cor só. O Medidor do relatório pinta de
   verde a vermelho conforme o valor, o que aqui sugeriria alternativa boa e
   ruim — por isso a barra é própria. */

const VISOES = [
  { id: "perguntas", rotulo: "Por pergunta" },
  { id: "alunos", rotulo: "Por aluno" }
];

const FILTROS = [
  { id: "todos", rotulo: "Todos" },
  { id: "realizaram", rotulo: "Responderam" },
  { id: "pendentes", rotulo: "Não responderam" }
];

export const formatarTempo = segundos => {
  if (segundos === null || segundos === undefined) return "—";
  const total = Math.max(0, Math.round(Number(segundos)));
  const minutos = Math.floor(total / 60);
  const resto = total % 60;
  if (!minutos) return `${resto}s`;
  return resto ? `${minutos}min ${resto}s` : `${minutos}min`;
};

const ordenarPorOrdem = itens =>
  [...itens].sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));

const Distribuicao = ({ pergunta, indice }) => {
  const opcoes = ordenarPorOrdem(pergunta.opcoes || []);

  return (
    <article className="stat-report__card form-report__question">
      <header className="form-report__question-header">
        <span className="form-report__question-number">
          Pergunta {indice + 1}
        </span>
        <span className="stat-report__room">
          {pergunta.respondidas || 0} resposta(s)
          {pergunta.sem_resposta > 0 &&
            ` · ${pergunta.sem_resposta} sem resposta`}
          {pergunta.tempo_medio !== null &&
            pergunta.tempo_medio !== undefined &&
            ` · tempo médio ${formatarTempo(pergunta.tempo_medio)}`}
        </span>
      </header>
      <div className="form-report__statement">
        <FormularioRichContent value={pergunta.enunciado} readOnly />
      </div>
      <ul className="form-report__options">
        {opcoes.map((opcao, opcaoIndice) => (
          <li className="form-report__option" key={opcao.id}>
            <span className="form-report__letter">
              {letraDaOpcao(opcaoIndice)}
            </span>
            <div className="form-report__option-body">
              <div className="form-report__option-text">
                <FormularioRichContent value={opcao.texto} readOnly />
              </div>
              <div className="form-report__bar">
                <span style={{ width: `${Number(opcao.percentual) || 0}%` }} />
              </div>
            </div>
            <span className="form-report__option-value">
              <strong>{`${Number(opcao.percentual || 0).toFixed(0)}%`}</strong>
              <small>{opcao.quantidade || 0}</small>
            </span>
          </li>
        ))}
      </ul>
    </article>
  );
};

/* O que o estudante escolheu em cada pergunta e quanto tempo levou.
   `respostas` vem como { question_id: option_id }, com option_id null quando
   o tempo acabou; `tempos`, como { question_id: segundos }, medido com ou sem
   limite de tempo. */
const RespostasDoAluno = ({ aluno, perguntas, opcoesPorId }) => (
  <ol className="form-report__answers">
    {perguntas.map((pergunta, indice) => {
      const respondeu = Object.prototype.hasOwnProperty.call(
        aluno.respostas || {},
        String(pergunta.id)
      );
      const opcaoId = respondeu ? aluno.respostas[String(pergunta.id)] : null;
      const opcao = opcaoId === null ? null : opcoesPorId[opcaoId];
      const segundos = aluno.tempos?.[String(pergunta.id)];

      return (
        <li className="form-report__answer" key={pergunta.id}>
          <span className="form-report__answer-number">{indice + 1}.</span>
          <div className="form-report__answer-statement">
            <FormularioRichContent value={pergunta.enunciado} readOnly />
          </div>
          <div className="form-report__answer-choice">
            {opcao ? (
              <>
                <span className="form-report__letter">{opcao.letra}</span>
                <FormularioRichContent value={opcao.texto} readOnly />
              </>
            ) : (
              <span className="stat-report__empty-value">
                {respondeu ? "Tempo esgotado" : "Sem resposta"}
              </span>
            )}
          </div>
          <span
            className="form-report__answer-time"
            title="Tempo nesta pergunta"
          >
            {respondeu ? formatarTempo(segundos) : "—"}
          </span>
        </li>
      );
    })}
  </ol>
);

const ResumoFormulario = ({ data }) => {
  const [visao, setVisao] = useState("perguntas");
  const [filtro, setFiltro] = useState("todos");
  const [aberto, setAberto] = useState(null);

  const alunos = useMemo(() => data?.alunos || [], [data]);
  const perguntas = useMemo(
    () => ordenarPorOrdem(data?.perguntas || []),
    [data]
  );
  const resumo = data?.resumo || {};
  const totalPerguntas = data?.formulario?.total_perguntas || 0;

  const opcoesPorId = useMemo(() => {
    const mapa = {};
    perguntas.forEach(pergunta =>
      ordenarPorOrdem(pergunta.opcoes || []).forEach((opcao, indice) => {
        mapa[opcao.id] = { texto: opcao.texto, letra: letraDaOpcao(indice) };
      })
    );
    return mapa;
  }, [perguntas]);

  const visiveis = useMemo(() => {
    if (filtro === "realizaram") return alunos.filter(aluno => aluno.realizou);
    if (filtro === "pendentes") return alunos.filter(aluno => !aluno.realizou);
    return alunos;
  }, [alunos, filtro]);

  const paginacao = usePaginacao(visiveis);

  if (!data) return <div className="stat-report">Carregando...</div>;

  const indicadores = [
    {
      label: "Participantes",
      valor: resumo.estudantes || 0,
      hint: `${totalPerguntas} pergunta(s) no formulário`
    },
    {
      label: "Responderam",
      valor: resumo.realizaram || 0,
      hint: `${Number(resumo.percentual_realizacao || 0).toFixed(
        0
      )}% dos participantes`
    },
    {
      label: "Tempo médio",
      valor: formatarTempo(resumo.tempo_medio),
      hint: "Entre quem respondeu"
    }
  ];

  const alternarAluno = userId =>
    setAberto(atual => (atual === userId ? null : userId));

  const situacao = aluno => (
    <Pilula estado={aluno.realizou ? "feito" : null}>
      {aluno.realizou ? "Respondido" : "Pendente"}
    </Pilula>
  );

  const identificacao = aluno => (
    <div className="stat-report__student">
      <Avatar foto={aluno.user_photo} />
      <div>
        <div className="stat-report__name">{aluno.user_name}</div>
        {/* Perfil e turma: o agendamento por público alcança professores e
            administradores, e não só estudantes */}
        {[...(aluno.perfis || []), ...(aluno.turmas || [])].length > 0 && (
          <div className="stat-report__room">
            {[...(aluno.perfis || []), ...(aluno.turmas || [])].join(" · ")}
          </div>
        )}
      </div>
    </div>
  );

  const botaoRespostas = aluno =>
    aluno.realizou && (
      <button
        type="button"
        className="stat-report__ghost"
        aria-expanded={aberto === aluno.user_id}
        onClick={() => alternarAluno(aluno.user_id)}
      >
        {aberto === aluno.user_id ? "Ocultar respostas" : "Ver respostas"}
      </button>
    );

  const porAluno = () => {
    if (!alunos.length) {
      return (
        <Vazio
          titulo="Ninguém para acompanhar ainda"
          texto="Agende o formulário ou use-o em uma atividade. As respostas aparecem aqui assim que as pessoas enviarem."
        />
      );
    }

    return (
      <>
        <div className="stat-report__toolbar">
          <Filtros opcoes={FILTROS} atual={filtro} onTrocar={setFiltro} />
        </div>

        {paginacao.naPagina.length === 0 ? (
          <Vazio
            titulo={
              filtro === "realizaram"
                ? "Ninguém respondeu o formulário ainda"
                : "Todos já responderam"
            }
            texto={
              filtro === "realizaram"
                ? "Assim que alguém enviar, as respostas aparecem aqui."
                : "Não há pendências nesta turma."
            }
          />
        ) : (
          <>
            <div className="stat-report__card">
              <table className="stat-report__table">
                <thead>
                  <tr>
                    <th>Participante</th>
                    <th>Respondidas</th>
                    <th>Situação</th>
                    <th>Tempo total</th>
                    <th>Respondido em</th>
                    <th aria-label="Respostas" />
                  </tr>
                </thead>
                <tbody>
                  {paginacao.naPagina.map(aluno => (
                    <Fragment key={aluno.user_id}>
                      <tr>
                        <td>{identificacao(aluno)}</td>
                        <td>
                          {aluno.realizou ? (
                            <span className="stat-report__score">
                              {aluno.respondidas} de {aluno.total}
                            </span>
                          ) : (
                            <span className="stat-report__empty-value">—</span>
                          )}
                        </td>
                        <td>{situacao(aluno)}</td>
                        <td>
                          <span className="stat-report__date">
                            {aluno.realizou
                              ? formatarTempo(aluno.tempo_total)
                              : "—"}
                          </span>
                        </td>
                        <td>
                          <span className="stat-report__date">
                            {aluno.realizou
                              ? formatarData(aluno.finalizada_em)
                              : "—"}
                          </span>
                        </td>
                        <td className="form-report__toggle-cell">
                          {botaoRespostas(aluno)}
                        </td>
                      </tr>
                      {aberto === aluno.user_id && (
                        <tr className="form-report__answers-row">
                          <td colSpan={6}>
                            <RespostasDoAluno
                              aluno={aluno}
                              perguntas={perguntas}
                              opcoesPorId={opcoesPorId}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="stat-report__list">
              {paginacao.naPagina.map(aluno => (
                <div className="stat-report__list-item" key={aluno.user_id}>
                  <div className="stat-report__list-top">
                    {identificacao(aluno)}
                    {situacao(aluno)}
                  </div>
                  <div className="stat-report__room">
                    {aluno.realizou
                      ? `${aluno.respondidas} de ${
                          aluno.total
                        } · ${formatarTempo(
                          aluno.tempo_total
                        )} · ${formatarData(aluno.finalizada_em)}`
                      : "Ainda não respondeu"}
                  </div>
                  {botaoRespostas(aluno)}
                  {aberto === aluno.user_id && (
                    <RespostasDoAluno
                      aluno={aluno}
                      perguntas={perguntas}
                      opcoesPorId={opcoesPorId}
                    />
                  )}
                </div>
              ))}
            </div>

            <Paginacao
              pagina={paginacao.pagina}
              totalPaginas={paginacao.totalPaginas}
              total={visiveis.length}
              primeiro={paginacao.primeiro}
              ultimo={paginacao.ultimo}
              onIr={paginacao.irPara}
            />
          </>
        )}
      </>
    );
  };

  const porPergunta = () =>
    perguntas.length ? (
      <div className="form-report__questions">
        {perguntas.map((pergunta, indice) => (
          <Distribuicao key={pergunta.id} pergunta={pergunta} indice={indice} />
        ))}
      </div>
    ) : (
      <Vazio
        titulo="Este formulário não tem perguntas"
        texto="Adicione perguntas ao formulário para acompanhar as respostas."
      />
    );

  return (
    <div className="stat-report">
      <Indicadores itens={indicadores} />

      <div className="stat-report__toolbar">
        <Filtros opcoes={VISOES} atual={visao} onTrocar={setVisao} />
      </div>

      {visao === "perguntas" ? porPergunta() : porAluno()}
    </div>
  );
};

export default ResumoFormulario;
